import { useState } from "react";
import { Link } from "react-router-dom";

import { describeApiError, isApiError } from "../../api/errors";
import {
  useCapabilities,
  usePackageManifest,
  useProfile,
  useResolveReviewItem,
  useScope,
  useMigration,
  useSignoff,
  useSignoffWatch,
  useSignoffVatLock,
} from "../../api/hooks";
import type { SignoffBody, SignoffVatLock } from "../../api/types";
import { Badge, Banner, Button, ConfirmDialog, Field } from "../../components";
import { t, tDynamic } from "../../i18n/t";
import type { NotMigratedRow } from "./NotMigrated";
import { rowName, type TotalRow } from "./TotalsTable";

/** Scope keys that record an operator override and must name the actor who recorded it (§B3.3, §B5.4 item 1). */
const OVERRIDE_KEYS = ["allow_non_empty", "accept_warnings", "allow_unbalanced"] as const;

export interface PointSummary {
  point: string;
  pass: number;
  fail: number;
}

interface PackageChanged {
  expected?: string;
  actual?: string;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

/**
 * xmg-lmux (1): the one sentence that says what LuciBook will lock. The months come from LuciBook's own dry-run
 * plan; `null` means no plan was recorded for this dry run.
 */
export function vatLockSentence(vatLock: SignoffVatLock): string {
  const months = vatLock.months;
  if (months === null || months === undefined) return t("signoff.vat_lock_unknown");
  if (months.length > 0) return t("signoff.vat_lock_months", { months: months.join(", ") });
  return vatLock.hmrc_attached ? t("signoff.vat_lock_none") : t("signoff.vat_lock_none_no_hmrc");
}

/** xmg-lmux (1): the acknowledgements the sign-off posts -- every open VAT_LOCK item the dialog showed. */
export function vatLockAcknowledgements(
  vatLock: SignoffVatLock | undefined,
): NonNullable<SignoffBody["vat_lock_acknowledgements"]> {
  return (vatLock?.items ?? []).flatMap((item) =>
    item.acknowledge_option === null || item.acknowledge_option === undefined
      ? []
      : [{ review_item_id: item.id, option: item.acknowledge_option }],
  );
}

/**
 * §B5.4. The moment of record. The body order is the design's (SignoffDialog.html), which supersedes §B5.4's
 * draft order: what is being signed (the hashes) first, then the frozen scope summary, the per-point PASS/FAIL
 * summary with every failing check named, "not migrated and why", then the preflight WARNs.
 *
 * (v4-M-10) There is no `currency` prop. The previous draft declared one, never destructured it, and rendered no
 * money at all — the dialog signs hashes and counts, not amounts. When §B5.4 later asks for a money line, the prop
 * comes back together with the `<Money>` that uses it.
 */
export function SignoffDialog({
  migrationId,
  open,
  onCancel,
  failingChecks,
  notMigrated,
  pointSummary,
  bankNames,
}: {
  migrationId: string;
  open: boolean;
  onCancel: () => void;
  failingChecks: readonly TotalRow[];
  notMigrated: readonly NotMigratedRow[];
  pointSummary: readonly PointSummary[];
  /** Source bank account id -> "code name" (xmg-lmux (2)): a failing bank row is named, not shown by its id. */
  bankNames?: ReadonlyMap<string, string>;
}): JSX.Element | null {
  const manifest = usePackageManifest(migrationId);
  const scope = useScope(migrationId);
  const profile = useProfile(migrationId);
  const capabilities = useCapabilities(migrationId);
  const signoff = useSignoff(migrationId);
  const signoffWatch = useSignoffWatch(migrationId, useMigration(migrationId).data?.state);
  const signoffBusy = signoff.isPending || signoffWatch.waiting;
  const vatLock = useSignoffVatLock(migrationId, open);
  const resolve = useResolveReviewItem(migrationId);
  const [signer, setSigner] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [changed, setChanged] = useState<PackageChanged | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  if (!open) {
    return null;
  }

  const scopeBody = (scope.data?.scope ?? {}) as Record<string, unknown>;
  const entities = Array.isArray(scopeBody.entities) ? (scopeBody.entities as string[]) : [];
  // `ProfileOut` names the list `issues` (§A8 row 26).
  const warns = (profile.data?.issues ?? []).filter((row) => row.severity === "WARN");
  const packageSha = asString(manifest.data?.package_sha256);
  const entries = (capabilities.data?.entries ?? {}) as Record<string, { support?: string } | undefined>;

  const vatLockItems = vatLock.data?.items ?? [];
  const showVatLock = vatLock.data !== undefined && (vatLock.data.applies || vatLockItems.length > 0);

  async function submit(): Promise<void> {
    if (signoffBusy) return;
    setChanged(null);
    setFailure(null);
    // §B5.4 posts `expected_package_sha256` and, xmg-lmux (1), the VAT-lock acknowledgements this dialog showed --
    // NOTHING else: `SignoffIn` forbids extra keys, so a `signer` in the body is a flat `422 validation_failed` and
    // the conversion can never be signed from the UI. The typed name is a local confirmation gate; the actor
    // recorded on the event is the session operator.
    const acknowledgements = vatLockAcknowledgements(vatLock.data);
    const body: SignoffBody = {
      ...(packageSha === null ? {} : { expected_package_sha256: packageSha }),
      ...(acknowledgements.length === 0 ? {} : { vat_lock_acknowledgements: acknowledgements }),
    };
    try {
      await signoff.mutateAsync(body);
      // §A9.5: nothing else is called. `signoff_migration_task` chains `advance_migration` itself.
      onCancel();
    } catch (error) {
      if (isApiError(error) && error.is("package_changed")) {
        setChanged(error.details as PackageChanged);
        return;
      }
      if (isApiError(error) && (error.is("blocking_reviews_remain") || error.is("validation_failed"))) {
        // e.g. a new VAT_LOCK item was filed after this dialog opened: show the refusal and the fresh notice.
        setFailure(describeApiError(error, t("error.unexpected")));
        void vatLock.refetch();
        return;
      }
      throw error;
    }
  }

  async function cancelInstead(itemId: string, option: string): Promise<void> {
    setFailure(null);
    try {
      // The dialog only opens at the conversion sign-off, so a cancel answer can move the migration.
      await resolve.mutateAsync({ itemId, body: { option }, mayMoveState: true });
      onCancel();
    } catch (caught) {
      setFailure(describeApiError(caught, t("error.unexpected")));
    }
  }

  return (
    <ConfirmDialog
      open
      title={t("signoff.title")}
      confirmLabel={signoffBusy ? t("signoff.working") : t("signoff.submit")}
      onCancel={onCancel}
      onConfirm={() => void submit()}
      confirmDisabled={
        signer.trim() === "" || !confirmed || changed !== null || vatLock.isLoading || signoffBusy
      }
    >
      {/* 1 — the hashes, monospace and selectable */}
      <section className="dialog__section">
        <span className="eyebrow">{t("signoff.hashes")}</span>
        <dl className="dialog__dl">
          <dt>{t("signoff.package_sha256")}</dt>
          <dd>
            <code data-testid="signoff-package-sha256">{packageSha ?? t("signoff.hash_pending")}</code>
          </dd>
          <dt>{t("signoff.scope_sha256")}</dt>
          <dd>
            <code data-testid="signoff-scope-sha256">
              {asString(manifest.data?.scope_sha256) ?? t("common.none")}
            </code>
          </dd>
          <dt>{t("signoff.mapping_version")}</dt>
          <dd>
            <code data-testid="signoff-mapping-version">
              {asString(manifest.data?.mapping_version_id) ?? t("common.none")}
            </code>
          </dd>
        </dl>
      </section>

      {/* 2 — the frozen scope summary */}
      <section className="dialog__section">
        <span className="eyebrow">{t("signoff.frozen_scope")}</span>
        <ul>
          {entities.map((entity) => {
            const support = entries[entity]?.support;
            return (
              <li key={entity} data-testid={`signoff-entity-${entity}`}>
                {entity}{" "}
                <Badge tone={support === "FULL" ? "pass" : support === "PARTIAL" ? "warn" : "neutral"}>
                  {support === undefined ? t("common.none") : tDynamic("support.", support)}
                </Badge>
              </li>
            );
          })}
        </ul>
        <p data-testid="signoff-history-tier">
          {t("signoff.history_tier", { tier: asString(scopeBody.history_tier) ?? t("common.unknown") })}
        </p>
        <p data-testid="signoff-extracted-as-at">
          {t("signoff.extracted_as_at", { date: asString(scopeBody.extracted_as_at) ?? t("common.unknown") })}
        </p>
        <ul>
          {OVERRIDE_KEYS.filter((key) => scopeBody[key] === true).map((key) => (
            <li key={key} data-testid={`signoff-override-${key}`}>
              {t("signoff.override", {
                key,
                actor: asString(scopeBody[`${key}_actor`]) ?? t("common.unknown"),
              })}
            </li>
          ))}
        </ul>
      </section>

      {/* 3 — per-point PASS/FAIL, and every failing check by name */}
      <section className="dialog__section">
        <span className={failingChecks.length === 0 ? "eyebrow" : "eyebrow eyebrow--fail"}>
          {t("signoff.failing")}
          {failingChecks.length === 0 ? "" : ` · ${failingChecks.length} ${t("signoff.failing_suffix")}`}
        </span>
        <ul>
          {pointSummary.map((row) => (
            <li key={row.point} data-testid={`signoff-point-${row.point}`}>
              {t("dryrun.group", { check: row.point, pass: row.pass, fail: row.fail })}
            </li>
          ))}
        </ul>
        <ul className="mono dialog__faillist">
          {failingChecks.map((row) => (
            <li
              key={`${row.point}-${row.check}-${row.key}`}
              className="dialog__failitem"
              data-testid={`signoff-failing-${row.point}-${row.check}-${row.key}`}
            >
              {/* xmg-lmux (2): named the way the totals table names it; the raw key only when there is no name. */}
              {t("signoff.failing_check", {
                point: row.point,
                check: row.check,
                key: rowName(row, bankNames) ?? row.key,
                status: row.status,
              })}
            </li>
          ))}
        </ul>
      </section>

      {/* 4 — "not migrated, and why", repeated here rather than merely present on the page behind */}
      <section className="dialog__section">
        <span className="eyebrow">{t("signoff.not_migrated")}</span>
        <ul>
          {notMigrated.map((row) => (
            <li key={row.entity}>{row.sentence}</li>
          ))}
        </ul>
      </section>

      {/* 5 — the preflight WARNs carried from profiling */}
      <section className="dialog__section">
        <span className="eyebrow">{t("signoff.preflight_warnings")}</span>
        {warns.length === 0 ? (
          <p>{t("signoff.preflight_empty")}</p>
        ) : (
          <ul>
            {warns.map((row) => (
              <li key={row.check}>{`${row.check}: ${row.message}`}</li>
            ))}
          </ul>
        )}
      </section>

      {/* 6 — xmg-lmux (1): the VAT-lock consequence; signing acknowledges every VAT_LOCK notice shown here */}
      {!showVatLock || vatLock.data === undefined ? null : (
        <section className="dialog__section" data-testid="signoff-vat-lock">
          <span className={vatLockItems.length === 0 ? "eyebrow" : "eyebrow eyebrow--fail"}>
            {t("signoff.vat_lock")}
          </span>
          <p data-testid="signoff-vat-lock-sentence">{vatLockSentence(vatLock.data)}</p>
          {(vatLock.data.already_locked_months ?? []).length === 0 ? null : (
            <p data-testid="signoff-vat-lock-already">
              {t("signoff.vat_lock_already", {
                months: (vatLock.data.already_locked_months ?? []).join(", "),
              })}
            </p>
          )}
          {vatLockItems.length === 0 ? null : (
            <ul>
              {vatLockItems.map((item) => (
                <li
                  key={item.id}
                  className="dialog__vatitem"
                  data-testid={`signoff-vat-lock-item-${item.id}`}
                >
                  <p>{item.message}</p>
                  {item.acknowledge_option === null || item.acknowledge_option === undefined ? null : (
                    <p className="muted">{t("signoff.vat_lock_ack", { option: item.acknowledge_option })}</p>
                  )}
                  {item.cancel_option === null || item.cancel_option === undefined ? null : (
                    <Button
                      className="button--wrap"
                      variant="danger"
                      disabled={resolve.isPending}
                      onClick={() => void cancelInstead(item.id, item.cancel_option as string)}
                    >
                      {t("signoff.vat_lock_cancel")}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <section className="dialog__section">
        {failure === null ? null : (
          <Banner tone="block">
            <span data-testid="signoff-failure">{failure}</span>
          </Banner>
        )}
        {changed === null ? null : (
          <Banner tone="block">
            <span data-testid="signoff-package-changed">
              {t("signoff.package_changed", {
                expected: changed.expected ?? t("common.unknown"),
                actual: changed.actual ?? t("common.unknown"),
              })}
            </span>{" "}
            {/* No retry: retrying here would sign a different package (§B5.4). */}
            <Link to={`/migrations/${migrationId}/dry-run`}>{t("signoff.review_again")}</Link>
          </Banner>
        )}

        <Field label={t("signoff.signer")} hint={t("signoff.signer_hint")}>
          {({ id, describedBy }) => (
            <input
              id={id}
              aria-describedby={describedBy}
              type="text"
              autoComplete="off"
              value={signer}
              onChange={(event) => setSigner(event.target.value)}
            />
          )}
        </Field>
        <Field label={t("signoff.confirm")}>
          {({ id, describedBy }) => (
            <input
              id={id}
              aria-describedby={describedBy}
              type="checkbox"
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
          )}
        </Field>
      </section>
    </ConfirmDialog>
  );
}
